import { useState } from 'react';
import { ColumnsType } from 'antd/es/table';
import { SearchNormal1, Menu } from 'iconsax-react';
import { Dropdown } from 'antd';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomTable from '../../../components/atoms/CustomTable';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CoinsPlan } from '../../../types/coins';

interface OffersSectionProps {
  data: CoinsPlan[];
  isLoading: boolean;
  totalElements: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onSearch: (value: string) => void;
  onAdd: () => void;
  onEdit: (item: CoinsPlan) => void;
  onDelete: (item: CoinsPlan) => void;
  onActivate: (item: CoinsPlan, active: boolean) => void;
}

export default function OffersSection({ 
  data, 
  isLoading, 
  totalElements, 
  currentPage, 
  onPageChange, 
  onSearch, 
  onAdd, 
  onEdit, 
  onDelete,
  onActivate
}: OffersSectionProps) {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const offerColumns: ColumnsType<CoinsPlan> = [
    { title: t('home.offer-name'), dataIndex: 'name', key: 'name' },
    { title: t('home.eq-price'), dataIndex: 'price', key: 'price', render: (val) => `${val} ${t('dispute.SAR', 'SAR')}` },
    {
      title: t('home.coins-amount-col'),
      dataIndex: 'coinsNo',
      key: 'coinsNo',
      render: (val) => `${val} ${t('home.coin-unit', 'Coin')}`,
    },
    { title: t('home.discount'), dataIndex: 'discount', key: 'discount', render: (val) => val ? `${val}%` : '-' },
    { title: t('home.added-by'), dataIndex: 'addedByName', key: 'addedByName', render: (val) => val || '-' },
    {
      title: t('home.date-applied'),
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (val) => (val ? dayjs(val).locale(i18n.language).format('DD MMM YYYY') : '-'),
    },
    {
      title: t('home.actions'),
      key: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'title',
                label: <span className="text-gray-400 font-medium">{t('home.plan-management')}</span>,
                disabled: true,
                className: '!cursor-default !bg-transparent',
                style: { pointerEvents: 'none' },
              },
              {
                key: 'activate',
                label: (
                  <div className="flex items-center justify-between w-full min-w-[160px] py-1" onClick={(e) => e.stopPropagation()}>
                    <span>{t('home.active-plan', 'Active plan')}</span>
                    <div 
                      className="relative inline-flex items-center cursor-pointer h-5 w-9"
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate(record, !record.active);
                      }}
                    >
                      {/* Track */}
                      <div className="absolute w-9 h-[14px] bg-[#E5E7EB] rounded-full top-1/2 -translate-y-1/2 transition-colors"></div>
                      {/* Thumb */}
                      <div 
                        className={`absolute w-5 h-5 rounded-full shadow-sm transition-transform duration-300 flex items-center justify-center ${
                          record.active 
                            ? 'translate-x-4 bg-light-success' 
                            : 'translate-x-0 bg-white border border-gray-200'
                        }`}
                      >
                        {record.active && (
                          <div className="w-[10px] h-[10px] border-[1.5px] border-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'edit',
                label: <div className="py-1">{t('home.edit-plan', 'Edit plan')}</div>,
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                label: <div className="py-1">{t('home.delete-plan', 'Delete plan')}</div>,
                onClick: () => onDelete(record),
              },
            ],
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <div className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center cursor-pointer transition-colors hover:bg-gray-100 mx-auto">
            <Menu size={16} className="text-gray-600" variant="Outline" />
          </div>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-xl p-6 flex flex-col gap-6 shadow-sm border border-gray-100">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-[#111928] text-2xl font-bold leading-none">
            {t('home.coins')}
          </h3>
          <SectionDesc text={t('home.coins-desc')} />
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <div className="w-full sm:w-64">
            <Input
              id="search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearch(e.target.value);
              }}
              placeholder={t('home.search-here')}
              CustomIcon={
                <SearchNormal1 size={18} className="text-gray-400" />
              }
              className="!rounded-xl"
            />
          </div>
          <Button
            variant="dark"
            className="w-full sm:w-auto rounded-xl px-6 py-2 whitespace-nowrap"
            onClick={onAdd}
          >
            {t('home.add-new-offer')}
          </Button>
        </div>
      </div>

      <CustomTable
        columns={offerColumns}
        dataSource={data}
        loading={isLoading}
        currentPage={currentPage}
        totalElements={totalElements}
        onPageChange={onPageChange}
        onRowClick={() => {}}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      />
    </div>
  );
}
